import { useEffect, useState } from "react";
import { ClipboardCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, Loading } from "../../components/shared";

const CHECKS = [
  "identity", "mobile", "email", "business_proof", "qualification", "experience",
  "services", "work_area", "licenses", "terms", "recommendation",
];

const emptyAssignment = { society_ids: "", pincodes: "", service_ids: "" };

export default function AdminProviderApplications() {
  const [applications, setApplications] = useState(null);
  const [selected, setSelected] = useState(null);
  const [checks, setChecks] = useState({});
  const [staff, setStaff] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [assignment, setAssignment] = useState(emptyAssignment);

  const providerIdentity = (app) => {
    if (!app) return null;
    const providerId = app.vendor_id || app.expert_id;
    return providerId ? { providerId, type: app.applicant_type } : null;
  };

  const load = async () => {
    try {
      const response = await api.get("/admin/provider-applications");
      setApplications(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load applications");
      setApplications([]);
    }
  };

  const loadAssignments = async (app) => {
    const provider = providerIdentity(app);
    if (!provider) {
      setAssignments([]);
      return;
    }
    const response = await api.get(
      `/admin/provider-applications/providers/${provider.type}/${provider.providerId}/assignments`,
    );
    setAssignments(response.data);
  };

  useEffect(() => {
    load();
    api.get("/admin/staff").then((response) => setStaff(response.data)).catch(() => setStaff([]));
  }, []);

  const open = async (app) => {
    try {
      const response = await api.get(`/admin/provider-applications/${app.application_id}`);
      const detail = response.data.application;
      setSelected(detail);
      setChecks(detail.verification_checks || {});
      setAssignee(detail.assigned_to || "");
      setDocuments(response.data.documents || []);
      await loadAssignments(detail);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load application details");
    }
  };

  const saveChecks = async () => {
    try {
      await api.post(`/admin/provider-applications/${selected.application_id}/checks`, { checks });
      toast.success("Verification checklist saved");
      await open(selected);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save checklist");
    }
  };

  const assignVerifier = async () => {
    try {
      await api.post(`/admin/provider-applications/${selected.application_id}/assign`, {
        staff_user_id: assignee,
      });
      toast.success("Verifier assignment saved");
      await open(selected);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not assign verifier");
    }
  };

  const approve = async () => {
    try {
      await api.post(`/admin/provider-applications/${selected.application_id}/approve`);
      toast.success("Application approved. A secure activation invitation was prepared.");
      await open(selected);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Approval is not available");
    }
  };

  const createAssignment = async () => {
    const provider = providerIdentity(selected);
    if (!provider) {
      toast.error("Approve this application before assigning societies");
      return;
    }
    try {
      await api.post(
        `/admin/provider-applications/providers/${provider.type}/${provider.providerId}/assignments`,
        {
          society_ids: assignment.society_ids.split(",").map((item) => item.trim()).filter(Boolean),
          pincodes: assignment.pincodes.split(",").map((item) => item.trim()).filter(Boolean),
          service_ids: assignment.service_ids.split(",").map((item) => item.trim()).filter(Boolean),
        },
      );
      setAssignment(emptyAssignment);
      await loadAssignments(selected);
      toast.success("Restrictive society assignment created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create society assignment");
    }
  };

  const updateAssignment = async (assignmentId, status) => {
    try {
      await api.patch(`/admin/provider-applications/assignments/${assignmentId}`, { status });
      await loadAssignments(selected);
      toast.success(`Assignment ${status.toLowerCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update assignment");
    }
  };

  if (!applications) return <Loading />;

  return (
    <div data-testid="provider-applications-admin-page">
      <PageHeader
        title="Provider Applications"
        subtitle="Verify applicants, approve activation, and restrict work to assigned societies."
      />
      <div className="grid gap-5 xl:grid-cols-[1.1fr_.9fr]">
        <section className="overflow-hidden border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="p-3 text-left">Application</th>
                <th className="p-3 text-left">Category</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {applications.length ? applications.map((app) => (
                <tr className="border-t" key={app.application_id}>
                  <td className="p-3"><b>{app.display_id}</b><br />{app.applicant_name}</td>
                  <td className="p-3">{app.applicant_type}</td>
                  <td className="p-3">{app.status}</td>
                  <td className="p-3"><Button data-testid={`provider-application-open-${app.application_id}`} onClick={() => open(app)} size="sm" variant="outline">Review</Button></td>
                </tr>
              )) : <tr><td className="p-6 text-center text-muted-foreground" colSpan="4">No provider applications yet.</td></tr>}
            </tbody>
          </table>
        </section>
        <aside className="border bg-card p-5" data-testid="provider-verification-panel">
          {selected ? <>
            <div className="flex gap-2"><ClipboardCheck /><div><b>{selected.display_id}</b><p className="text-sm text-muted-foreground">{selected.applicant_name}</p></div></div>
            <div className="mt-4"><label className="text-sm font-medium">Assign verification staff</label><div className="mt-2 flex gap-2"><select className="min-w-0 flex-1 border p-2 text-sm" data-testid="provider-verifier-select" onChange={(event) => setAssignee(event.target.value)} value={assignee}><option value="">Unassigned</option>{staff.map((member) => <option key={member.user_id} value={member.user_id}>{`${member.name} · ${member.email}`}</option>)}</select><Button data-testid="provider-assign-verifier" onClick={assignVerifier} type="button" variant="outline">Assign</Button></div></div>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">{CHECKS.map((key) => <label className="flex items-center gap-2 text-sm" key={key}><input checked={String(checks[key] || "").toUpperCase() === "VERIFIED"} data-testid={`provider-check-${key}`} onChange={(event) => setChecks({ ...checks, [key]: event.target.checked ? "VERIFIED" : "PENDING" })} type="checkbox" />{key.replace(/_/g, " ")}</label>)}</div>
            <div className="mt-5"><p className="text-sm font-medium">Private documents</p>{documents.map((document) => <a className="mt-2 block text-sm text-primary underline" data-testid={`provider-document-${document.document_id}`} href={`${API}/admin/provider-applications/documents/${document.document_id}/download`} key={document.document_id} rel="noreferrer" target="_blank">{document.doc_type} · {document.filename}</a>)}</div>
            <Button className="mt-5" data-testid="provider-save-checklist" onClick={saveChecks}>Save verification checklist</Button>
            <Button className="mt-3" data-testid="provider-approve-application" onClick={approve} variant="outline"><ShieldCheck className="mr-2 h-4 w-4" />Approve & send activation</Button>
            <section className="mt-6 border-t pt-5" data-testid="provider-assignment-panel"><h3 className="font-display font-bold">Restrictive society assignment</h3><p className="mt-1 text-xs text-muted-foreground">Providers see society requirements only after an active assignment.</p><div className="mt-3 space-y-2"><Input data-testid="provider-assignment-societies" onChange={(event) => setAssignment({ ...assignment, society_ids: event.target.value })} placeholder="Society IDs, comma separated" value={assignment.society_ids} /><Input data-testid="provider-assignment-pincodes" onChange={(event) => setAssignment({ ...assignment, pincodes: event.target.value })} placeholder="Approved PIN codes, comma separated" value={assignment.pincodes} /><Input data-testid="provider-assignment-services" onChange={(event) => setAssignment({ ...assignment, service_ids: event.target.value })} placeholder="Approved service IDs, comma separated" value={assignment.service_ids} /><Button data-testid="provider-create-assignment" onClick={createAssignment} size="sm" type="button">Assign societies</Button></div><div className="mt-4 space-y-2">{assignments.map((item) => <div className="border p-3 text-xs" key={item.assignment_id}><b>{item.status}</b><p className="mt-1">{item.society_ids.join(", ")}</p>{item.status === "ACTIVE" ? <Button data-testid={`provider-suspend-assignment-${item.assignment_id}`} onClick={() => updateAssignment(item.assignment_id, "SUSPENDED")} size="sm" variant="outline">Suspend</Button> : <Button data-testid={`provider-reactivate-assignment-${item.assignment_id}`} onClick={() => updateAssignment(item.assignment_id, "ACTIVE")} size="sm" variant="outline">Reactivate</Button>}<Button className="ml-2" data-testid={`provider-revoke-assignment-${item.assignment_id}`} onClick={() => updateAssignment(item.assignment_id, "REVOKED")} size="sm" variant="destructive">Revoke</Button></div>)}</div></section>
          </> : <p className="text-sm text-muted-foreground">Select an application to review, approve, and assign.</p>}
        </aside>
      </div>
    </div>
  );
}